import './DesignDefault.css';
import CheckoutPage from './components/CheckoutPage/index.tsx';
import {
  Alert,
  Box,
  CircularProgress,
  createTheme,
  CssBaseline,
  GlobalStyles,
  ThemeProvider as MuiThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { type ShopifyOfferDto, StoreColorThemeModeEnum } from './offer.types.ts';
import ErrorContext, {
  type AddErrorFunction,
  type AddErrorFunctionOptions,
  type DismissErrorFunction,
  type DisplayableError,
  type ErrorContextType,
} from './contexts/ErrorContext.tsx';
import { CheckoutError } from './services/checkout/checkout.types.ts';
import type { ThankYouByTokenResponseDto } from './services/checkout/checkout.types.ts';
import { fetchCheckoutOffer, getThankYouByToken } from './services/checkout/checkout.service.ts';
import { getCartCode } from './utils/cartStorage.ts';
import { AnalyticsProvider } from './services/tracking';
import { setOfferNanoIdMeta } from './services/tracking/offerNanoIdMeta';
import {
  DEFAULT_CHECKOUT_FAVICON_API_PATH,
  resolveCheckoutAssetUrl,
} from './utils/checkoutTheme';

const MAX_ERRORS = 5;

function App() {
  const [loading, setLoading] = useState(true);
  const [offerUuid, setOfferUuid] = useState<string | null>(null);
  const [shopifyOffer, setShopifyOffer] = useState<ShopifyOfferDto | null>(null);
  const [initialEditingSection, setInitialEditingSection] = useState<string | null>(null);
  const [thankYouToken, setThankYouToken] = useState<string | null>(null);
  const [thankYouData, setThankYouData] = useState<ThankYouByTokenResponseDto | null>(null);
  const [thankYouError, setThankYouError] = useState<string | null>(null);

  const [errorMessages, setErrorMessages] = useState<DisplayableError[]>([]);
  const [systemColorThemeMode, setSystemColorThemeMode] = useState<StoreColorThemeModeEnum | undefined>();

  // Detect system theme preference for MUI
  let prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  if (systemColorThemeMode) {
    switch (systemColorThemeMode) {
      case StoreColorThemeModeEnum.DARK:
        prefersDarkMode = true;
        break;
      case StoreColorThemeModeEnum.LIGHT:
        prefersDarkMode = false;
        break;
    }
  }

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDarkMode ? 'dark' : 'light',
          background: {
            default: prefersDarkMode ? '#111827' : '#F3F4F6', // Tailwind page-bg-dark and page-bg-light
            paper: prefersDarkMode ? '#1F2937' : '#FFFFFF',    // Tailwind card-bg-dark and card-bg-light
          },
          text: {
            primary: prefersDarkMode ? '#F9FAFB' : '#1F2937', // Tailwind gray-50 and text-primary
            secondary: prefersDarkMode ? '#D1D5DB' : '#4B5563',// Tailwind gray-300 and text-secondary
          },
          primary: {
            main: shopifyOffer?.checkoutTheme?.checkout?.config?.general?.buttonColor ?? '#3B82F6',
          },
          secondary: {
            main: '#EC4899',
          },
          success: {
            main: '#10B981', // Tailwind success
          },
        },
        typography: {
          fontFamily: 'Rubik, Poppins, sans-serif',
        },
      }),
    [prefersDarkMode, shopifyOffer],
  );


  // ✅ Função para determinar etapa baseada nos dados do cart
  const determineInitialStep = useCallback((cartData: any): string | null => {
    if (!cartData.customerData) return null;
    
    const { customerData } = cartData;
    
    // Etapa 1: Só dados básicos (nome, email, telefone)
    const hasBasicData = customerData.name && customerData.email && customerData.phone;
    if (!hasBasicData) return null;
    
    // Etapa 2: Dados básicos + endereço completo
    const hasAddress = customerData.address && 
      customerData.address.street && 
      customerData.address.city && 
      customerData.address.state && 
      customerData.address.postcode;
    
    if (!hasAddress) return 'SHIPPING';
    
    // Etapa 3: Dados completos (básicos + endereço + pagamento)
    // Por enquanto, se tem endereço completo, vai para pagamento
    return 'PAYMENT';
  }, []);

  // Get UUID from the URL with intelligent detection
  {
    const pathSegments = location.pathname.split('/').filter(p => p !== '');
    let uuid = '';
    let nextThankYouToken: string | null = null;
    
    // Detect if it's a thank-you URL
    if (pathSegments.length >= 3 && pathSegments[0] === 'checkout' && pathSegments[1] === 'thank-you') {
      nextThankYouToken = decodeURIComponent(pathSegments[2] || '');
    } else if (pathSegments.length >= 2 && pathSegments[0] === 'cart') {
      // Detect if it's a cart recovery URL
      // URL: /cart/CART-ACT5EVIQ
      uuid = pathSegments[1];
    } else if (pathSegments.length >= 1) {
      // URL: /tq0DK5wf7J/ (oferta normal)
      uuid = pathSegments[0];
    }
    
    // Fallback para desenvolvimento
    if (!nextThankYouToken && !uuid.length && import.meta.env.VITE_DEVELOPMENT_MODE === 'true') {
      uuid = 'RrsLr3FtLw';
    }

    if (nextThankYouToken !== thankYouToken) {
      setThankYouToken(nextThankYouToken);
      setThankYouData(null);
      setThankYouError(null);
    }

    if (!nextThankYouToken && uuid !== offerUuid) {
      setOfferUuid(uuid);
      setLoading(true);
      setShopifyOffer(null);
              setSystemColorThemeMode(undefined);
    }
  }

  const createErrorFinder = (error?: Error, idOrError?: string | Error | DisplayableError | AddErrorFunctionOptions): ((err: DisplayableError) => boolean) => {
    function findAgain() {
      if (typeof idOrError === 'undefined') {
        return () => false;
      }
      if (typeof idOrError === 'string') {
        return (err: DisplayableError) => err.id === idOrError;
      }
      if (idOrError instanceof Error) {
        return (err: DisplayableError) => err.error === idOrError;
      }
      if ('id' in idOrError) {
        return (err: DisplayableError) => err === idOrError;
      }
      // @ts-ignore
      if ('message' in idOrError) {
        // @ts-ignore
        return (err: DisplayableError) => err.options?.message === idOrError.message;
      }
      // @ts-ignore
      if ('tag' in idOrError) {
        // @ts-ignore
        return (err: DisplayableError) => err.options?.tag === idOrError.tag;
      }
      // @ts-ignore
      if ('category' in idOrError) {
        // @ts-ignore
        return (err: DisplayableError) => err.options?.category === idOrError.category;
      }
      return () => false;
    }

    function tryFind() {
      if (!error) {
        return () => false;
      }
      return (err: DisplayableError) => (err.error === error) || (err.error?.message === error.message) || (err.error?.name === error.name);
    }

    return (err: DisplayableError) => tryFind()(err) || findAgain()(err);
  };

  const addError: AddErrorFunction = useCallback((error: CheckoutError | Error, options?: AddErrorFunctionOptions) => {

    function createDisplayError(): DisplayableError {
      let displayError = {
        error,
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        timestamp: Date.now(),
        message: options?.message ?? error.message ?? 'Ocorreu um erro desconhecido.',
        name: error.name ?? 'Erro Desconhecido',
        options: options ?? {},
      } as DisplayableError;

      if (error instanceof CheckoutError) {
        displayError.details = error.details;
      } else { // Generic Error or other types
        displayError.details = (error as any)?.cause ? { cause: (error as any).cause, stack: error.stack } : error;
      }
      return displayError;
    }

    setErrorMessages(prev => {
      const findError = createErrorFinder(error, options);
      return prev.some(err => findError(err)) ? prev
        : [createDisplayError(), ...prev.slice(0, MAX_ERRORS - 1)];
    });

  }, [setErrorMessages]);

  const dismissError: DismissErrorFunction = useCallback((idOrError: string | Error | DisplayableError | AddErrorFunctionOptions) => {
    const findError = createErrorFinder(undefined, idOrError);
    setErrorMessages(prev => prev.filter(err => !findError(err)));
  }, [setErrorMessages]);

  const errorContextValue = useMemo<ErrorContextType>(() => ({
    addError,
    dismissError,
  }), [addError, dismissError]);

  useEffect(() => {
    if (!thankYouToken) return;

    setLoading(true);
    getThankYouByToken(thankYouToken)
      .then((data) => {
        setThankYouData(data);
        setOfferUuid(data.offerNanoId);
      })
      .catch(() => {
        setThankYouError('Token inválido ou expirado.');
        setLoading(false);
      });
  }, [thankYouToken]);

  // Fetch offer data based on the UUID
  useEffect(() => {
    const fetchOfferData = async (offerUuidParam: string, isCartRecovery: boolean = false) => {
      if (isCartRecovery) {
        // Recupera carrinho abandonado
        const sUrl = `${import.meta.env.VITE_BACKEND_URL}/carts/${offerUuidParam}/recover`;
        const response = await fetch(sUrl);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return await response.json();
      }

      // Oferta normal — se houver cartCode salvo, backend já devolve com bumps aceitos somados.
      const storedCartCode = getCartCode() ?? undefined;
      return fetchCheckoutOffer(offerUuidParam, storedCartCode);
    };

    if (!offerUuid) {
      if (thankYouToken) {
        return;
      }
      addError(new Error('ID da oferta não encontrado na URL'));
      setLoading(false);
      return;
    }

    // Detecta se é recuperação de carrinho baseado na URL
    const pathSegments = location.pathname.split('/').filter(p => p !== '');
    const isCartRecovery = pathSegments.length >= 2 && pathSegments[0] === 'cart';

    setLoading(true);
    fetchOfferData(offerUuid, isCartRecovery)
      .then((data: ShopifyOfferDto) => {
        dismissError('fetching offer');
        setShopifyOffer(data);

        // ✅ Se for cart recuperado, determina etapa inicial
        if (isCartRecovery && (data as any).customerData) {
          // ✅ Prioridade: currentStep do carrinho > determineInitialStep
          const cartCurrentStep = (data as any).currentStep;
          const initialStep = cartCurrentStep || determineInitialStep(data);
          if (initialStep) {
            setInitialEditingSection(initialStep);
          }
        }

        // Salva dados da oferta no sessionStorage para o AnalyticsProvider
        sessionStorage.setItem('shopifyOffer', JSON.stringify(data));

        // NanoId para dedupe/analytics: meta no head + sessionStorage (sem mexer na URL)
        setOfferNanoIdMeta(data.nanoId);

        // Setup system color theme mode for Store
        setSystemColorThemeMode(data.store.systemColorThemeMode);
        window.sessionStorage.setItem('systemColorThemeMode', data.store.systemColorThemeMode ?? '');
        window.localStorage.setItem('prefersDarkMode', String(prefersDarkMode));
        const tailwind = (window as any).tailwind;
        tailwind.config.darkMode = data.store.systemColorThemeMode === StoreColorThemeModeEnum.LIGHT
          ? data.store.systemColorThemeMode : 'media';

        // Cor de destaque do checkout vem do tema publicado (store_checkouts), não de store.*.
        const buttonColor = data.checkoutTheme?.checkout?.config?.general?.buttonColor;
        if (buttonColor) tailwind.config.theme.extend.colors.primary = buttonColor;

        // Só favicon do tema publicado; vazio/placeholder → default do backend.
        const faviconHref = resolveCheckoutAssetUrl(
          data.checkoutTheme?.checkout?.config?.general?.favicon,
          DEFAULT_CHECKOUT_FAVICON_API_PATH,
        );
        if (faviconHref) {
          document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]').forEach((el) => {
            el.href = faviconHref;
            if (faviconHref.endsWith('.webp')) el.type = 'image/webp';
          });
        }

      })
      .catch((err) => {
        console.error('Error fetching offer:', err);
        addError(err, { message: 'Failed to load offer data', category: 'network', tag: 'fetching offer', severity: 'error' });
      })
      .finally(() => setLoading(false));
  }, [offerUuid, addError, dismissError, thankYouToken]);

  useEffect(() => {
    if (!shopifyOffer?.store?.active) return;
    document.title = `Checkout ${shopifyOffer.name} | ${shopifyOffer.store.name}`;
  }, [shopifyOffer]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  if (thankYouError) {
    return <Alert severity="warning">Nao encontramos seu pedido. Verifique o link ou entre em contato com o suporte.</Alert>;
  }

  if (!shopifyOffer) {
    if (!offerUuid || !offerUuid.length) {
      return <Alert severity="warning">Não foi possível identificar a oferta. Por favor, verifique o ID.</Alert>;
    }
    return <Alert severity="error">Oferta com ID '{offerUuid}' não encontrada.</Alert>;
  }

  if (!shopifyOffer.checkoutTheme?.checkout?.config) {
    return (
      <Alert severity="error">
        Tema de checkout indisponível para esta loja. Publique um checkout no admin ou tente novamente.
      </Alert>
    );
  }

  return (
    <BrowserRouter>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline /> {/* Ensures MUI theme background and text colors are applied globally */}
        <AnalyticsProvider>
          <ErrorContext.Provider value={errorContextValue}>
            <GlobalStyles
              styles={`@tailwind base; @tailwind components; @tailwind utilities; @tailwind dark; ${shopifyOffer?.store?.design?.css ?? ''} ; ${shopifyOffer?.store?.checkoutTemplate ?? ''}`} />
            <CheckoutPage
              shopifyOffer={shopifyOffer}
              initialEditingSection={initialEditingSection}
              thankYouData={thankYouData}
              checkoutConfigOverride={shopifyOffer.checkoutTheme.checkout.config}
              paymentBadgesOverride={shopifyOffer.checkoutTheme.paymentBadges}
              storeContactOverride={shopifyOffer.checkoutTheme.storeContact}
            />
          </ErrorContext.Provider>
        </AnalyticsProvider>
      </MuiThemeProvider>
    </BrowserRouter>
  );
}

export default App;